// src/components/Admin/SnackableCourse/renderers/SafeHtml.tsx

import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import DOMPurify from 'dompurify';

interface Props {
  html: string;
  className?: string;
}

export const SafeHtml: React.FC<Props> = ({ html, className }) => {
  const sanitizedHtml = useMemo(() => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'a',
        'abbr',
        'b',
        'blockquote',
        'br',
        'code',
        'div',
        'em',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'hr',
        'i',
        'img',
        'li',
        'ol',
        'p',
        'pre',
        'span',
        'strong',
        'sub',
        'sup',
        'table',
        'tbody',
        'td',
        'th',
        'thead',
        'tr',
        'u',
        'ul',
        'link',
        'style',
        'section',
        'article',
        'aside',
        'header',
        'footer',
        'nav',
        'figure',
        'figcaption',
      ],
      ALLOWED_ATTR: [
        'href',
        'src',
        'alt',
        'title',
        'class',
        'id',
        'style',
        'target',
        'rel',
        'width',
        'height',
        'colspan',
        'rowspan',
        'data-*',
        'aria-*',
      ],
      ALLOW_DATA_ATTR: true,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel|data|ftp):/i,
    });
  }, [html]);

  return (
    <Box
      className={['safe-html', className].filter(Boolean).join(' ')}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      sx={{
        '& .hbr-type-h2': {
          fontSize: '1.75rem',
          fontWeight: 600,
          marginBottom: 2,
        },
        '& .hbr-type-body': {
          fontSize: '1rem',
          lineHeight: 1.6,
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
        },
        '& a': {
          color: 'primary.main',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },

        // ✅ Table wrapping fixes (Harbor + generic tables)
        '& table': {
          width: '100%',
          tableLayout: 'fixed', // key: prevents columns expanding forever
          borderCollapse: 'collapse',
        },
        '& th, & td': {
          whiteSpace: 'normal',
          overflowWrap: 'anywhere', // best wrap behavior for long strings/URLs
          wordBreak: 'break-word',  // fallback
        },

        // Harbor-specific classes in your sample HTML
        '& .hbr-css__table-loading-cell': {
          whiteSpace: 'normal !important',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        },
        '& .hbr-css__table': {
          width: '100%',
          tableLayout: 'fixed',
        },
      }}
    />
  );
};